<template>
    <HeadlessTransitionRoot
        appear
        :show="isOpen"
        as="template"
    >
        <HeadlessDialog
            as="div"
            class="relative z-50"
            :initialFocus="searchInput"
            @close="close"
        >
            <HeadlessTransitionChild
                as="template"
                enter="duration-300 ease-out"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="duration-200 ease-in"
                leave-from="opacity-100"
                leave-to="opacity-0"
            >
                <div class="fixed inset-0 bg-black bg-opacity-25">
                    <MouseFollower :hide-when-over-el="dialogPanelEl" />
                </div>
            </HeadlessTransitionChild>

            <div class="fixed bottom-0 left-0 top-0 w-screen overflow-x-hidden">
                <div class="min-h-full">
                    <HeadlessTransitionChild
                        as="template"
                        enter="duration-300 ease-out"
                        enter-from="opacity-0 translate-x-full"
                        enter-to="opacity-100 translate-x-0"
                        leave="duration-200 -mr-[17px] ease-in"
                        leave-from="opacity-100 translate-x-0"
                        leave-to="opacity-0 translate-x-full"
                    >
                        <HeadlessDialogPanel class="absolute right-0 w-full max-w-xl bg-whitegray shadow-xl transition-all overflow-y-hidden">
                            <div
                                ref="dialogPanelEl"
                                class="w-full"
                            >
                                <Transition
                                    name="fade"
                                    mode="out-in"
                                >
                                    <div class="flex flex-col">
                                        <div class="pt-4 px-4 lg:pt-7 lg:px-7 h-screen pb-16">
                                            <div class="flex items-center mb-4 lg:mb-7 gap-4 lg:gap-7">
                                                <button class="lg:hidden" @click="close">
                                                    <IconBack />
                                                </button>
                                                <div class="relative w-full">
                                                    <input
                                                        id="query"
                                                        v-model="searchValue"
                                                        class="peer w-full select-none rounded-xl border-2 border-transparent bg-white pt-6 pb-4 px-4 lg:text-base placeholder-transparent shadow-main outline-none transition-colors focus:border-orange-200 text-sm"
                                                        type="text"
                                                        placeholder="Поиск"
                                                        ref="searchInput"
                                                    />
                                                    <label
                                                        for="query"
                                                        class="pointer-events-none absolute left-4 top-3 select-none text-xs text-[#777675] transition-all peer-placeholder-shown:top-5 peer-placeholder-shown:text-base peer-focus:top-3 peer-focus:text-xs"
                                                    >
                                                        Поиск
                                                    </label>
                                                    <button
                                                        class="absolute right-4 top-1/2 -translate-y-1/2"
                                                        type="button"
                                                        v-if="searchValue.length > 0"
                                                        @click="searchValue = ''"
                                                    >
                                                        <img src="~/assets/icon/icon-close-gray-black.svg" alt="" class="h-6 lg:h-8 rounded-full bg-black opacity-20">
                                                    </button>
                                                    <IconSearchColor v-if="searchValue.length == 0" class="absolute right-4 top-1/2 -translate-y-1/2" />
                                                </div>
                                            </div>

                                            <div class="flex gap-3 flex-wrap" v-if="searchValue.length == 0">
                                                <button class="accent-gradient-bg rounded-full"
                                                        v-for="category in categories"
                                                        @click="searchValue = category.name.toLowerCase()"
                                                >
                                                    <div class="rounded-full border-2 border-transparent bg-clip-padding px-4 py-2 transition-colors uppercase bg-white text-black text-xs lg:text-md">{{category.name}}</div>
                                                </button>
                                            </div>

                                            <div class="relative overflow-y-auto bg-whitegray h-full p-4 -m-4" v-if="searchValue.length > 0 && filteredDishes.length > 0">
                                                <DishCard
                                                    :dish="dish"
                                                    size="row"
                                                    class="mb-4"
                                                    v-for="dish in filteredDishes"
                                                />
                                            </div>

                                            <div v-if="searchValue.length > 0 && filteredDishes.length == 0" class="flex grow items-center justify-center text-[#777675] h-full">
                                                По вашему запросу<br /> ничего не найдено
                                            </div>
                                        </div>
                                    </div>
                                </Transition>
                            </div>
                        </HeadlessDialogPanel>
                    </HeadlessTransitionChild>
                </div>
            </div>
        </HeadlessDialog>
    </HeadlessTransitionRoot>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useDishesStore } from '~/store/dishes'
import { includes } from 'lodash-es'
import { useAllDishes } from '~/composables/api/dishes'

const props = defineProps<{
    isOpen: boolean
}>()

const emit = defineEmits(['close'])
const { isOpen } = toRefs(props)

const dialogPanelEl = ref<HTMLElement>()
const searchInput = ref(null)
const searchValue = ref("")
const throttledQuery = refThrottled(searchValue, 1000, undefined, false)
const {getAll: getAllDishes, add: addDishToStore} = useDishesStore()

const { data: categories, suspense: suspenseCategories } = useCategories((v) => v.list)

const isEnabled = ref(false)
let dishes = getAllDishes()
const { data: dishesData } = useAllDishes(isEnabled)
watch(isOpen, () =>{
    if (dishes.value.length < 100) {
        isEnabled.value = true
    }
})

const filteredDishes = computed(() => {
    let foundCategories = []
    if(categories.value) {
        foundCategories = categories.value.filter(category => {
            return category.name?.toLowerCase() == searchValue.value.toLowerCase()
        }).map(el => el.id)
    }

    return dishes.value.filter((dish) => {
        return dish.name?.toLowerCase().includes(searchValue.value.toLowerCase()) || includes(foundCategories, dish?.category )
    })
})

watch(throttledQuery, () => {

    /*let dishes = getAllDishes()

    dishes.value.forEach((item) => {
        console.log(item)
    })*/
})

const close = () => {
    emit("close")
    isOpen.value = false
}

</script>